import { VentMonitorModel, VentSettingModel } from "@/models";

interface VentFieldDefinitionInfo<TVentModel> {
  label: string;
  units?: keyof TVentModel;
}

export interface VentFieldInfo<TVentModel> extends VentFieldDefinitionInfo<TVentModel> {
  field: keyof TVentModel;
}

type VentFieldDefinitions<TVentModel> = Readonly<
  { [field in keyof TVentModel]: Readonly<VentFieldDefinitionInfo<TVentModel>> }
>;

export const DefaultVentMonitorFields: Array<keyof VentMonitorModel> = [
  "minuteVentilation",
  "vte",
  "totalRate",
  "fiO2",
  "peep",
  "pMean",
  "pPeak",
  "spontaneousRate",
  "rsbi",
  "spontaneousTidalVolume",
  "mandatoryVte",
  "spontaneousVe",
  "timeExpiration",
  "timeInspiration",
  "vti",
  "workOfBreathingMeasured",
  "cdyn",
  "mandatoryRate",
  "pefr",
  "pifr",
  "etCO2"
];

export const BellavistaVentMonitorFields: Array<keyof VentMonitorModel> = [
  "minuteVentilation",
  "vte",
  "totalRate",
  "fiO2",
  "peep",
  "pMean",
  "pPeak",
  "spontaneousRate",
  "rsbi",
  "spontaneousTidalVolume",
  "timeExpiration",
  "timeInspiration",
  "vti",
  "cdyn",
  "pefr",
  "pifr",
  "etCO2",
  "autoPeep",
  "cstat",
  "flow",
  "ie",
  "leak",
  "leakFlow",
  "mvInsp",
  "p100",
  "pplat",
  "spO2",
  "percentSpon",
  "woBi",
  "mip"
];

export const DefaultVentAlarmFields: Array<keyof VentSettingModel> = [
  "rateHigh",
  "vteHigh",
  "vteLow",
  "veLow",
  "veHigh",
  "pPeakLow",
  "pPeakHigh",
  "peepLow",
  "apneaInterval"
];

export const BellavistaVentAlarmFields: Array<keyof VentSettingModel> = [
  "rateLow",
  "rateHigh",
  "vteHigh",
  "vteLow",
  "veLow",
  "veHigh",
  "pPeakLow",
  "pPeakHigh",
  "apneaInterval",
  "etCO2Low",
  "etCO2High",
  "fiO2Low",
  "fiO2High",
  "spO2Low",
  "spO2High"
];

export const DefaultVentSettingFields: Array<keyof VentSettingModel> = [
  "inspirationTime",
  "volume",
  "inspirationPressure",
  "flowPeak",
  "inspirationPause",
  "psv",
  "pressureLow",
  "pressureHigh",
  "timeLow",
  "timeHigh"
];

export const BellavistaVentSettingFields: Array<keyof VentSettingModel> = [
  "rate",
  "peep",
  "flowTrigger",
  "fiO2",
  "inspirationTime",
  "volume",
  "inspirationPressure",
  "inspirationPause",
  "psv",
  "pressureLow",
  "pressureHigh",
  "timeLow",
  "timeHigh",
  "psvTmax",
  "expTrigMode",
  "expTrigValue",
  "waveform",
  "riseMode",
  "riseValue",
  "height",
  "minVol",
  "flow"
];

const ventMonitorFields: VentFieldDefinitions<VentMonitorModel> = {
  cdyn: { label: "Cdyn", units: "cdynUnits" },
  etCO2: { label: "EtCO₂", units: "etCO2Units" },
  fiO2: { label: "FiO₂", units: "fiO2Units" },
  mandatoryRate: { label: "Mand Rate", units: "mandatoryRateUnits" },
  mandatoryVte: { label: "Mand Vte", units: "mandatoryVteUnits" },
  minuteVentilation: { label: "Minute Ventilation", units: "minuteVentilationUnits" },
  peep: { label: "PEEP", units: "peepUnits" },
  pefr: { label: "PEFR", units: "pefrUnits" },
  pifr: { label: "PIFR", units: "pifrUnits" },
  pMean: { label: "Pmean", units: "pMeanUnits" },
  pPeak: { label: "Ppeak", units: "pPeakUnits" },
  rsbi: { label: "RSBI", units: "rsbiUnits" },
  spontaneousRate: { label: "Spon Rate", units: "spontaneousRateUnits" },
  spontaneousTidalVolume: { label: "Spon TV", units: "spontaneousTidalVolumeUnits" },
  spontaneousVe: { label: "Spon Ve", units: "spontaneousVeUnits" },
  timeExpiration: { label: "Te", units: "timeExpirationUnits" },
  timeInspiration: { label: "Ti", units: "timeInspirationUnits" },
  totalRate: { label: "Total Rate", units: "totalRateUnits" },
  vte: { label: "Vte", units: "vteUnits" },
  vti: { label: "Vti", units: "vtiUnits" },
  workOfBreathingMeasured: { label: "WOB Measured", units: "workOfBreathingMeasuredUnits" },
  autoPeep: { label: "Auto PEEP", units: "autoPeepUnits" },
  cstat: { label: "CStat", units: "cstatUnits" },
  p100: { label: "P01", units: "p100Units" },
  pplat: { label: "Pplat", units: "pplatUnits" },
  spO2: { label: "SpO₂", units: "spO2Units" },
  ie: { label: "I:E" },
  mip: { label: "NIF", units: "mipUnits" },
  flow: { label: "Flow", units: "flowUnits" },
  leakFlow: { label: "Leak Flow", units: "leakFlowUnits" },
  mvInsp: { label: "MVinsp", units: "mvInspUnits" },
  percentSpon: { label: "%Spont", units: "percentSponUnits" },
  leak: { label: "Leak", units: "leakUnits" },
  woBi: { label: "WOBimp", units: "woBiUnits" }
};

const ventSettingFields: VentFieldDefinitions<VentSettingModel> = {
  fiO2: { label: "FiO₂", units: "fiO2Units" },
  flowTrigger: { label: "Flow Trig", units: "flowTriggerUnits" },
  inspirationPause: { label: "Insp Pause", units: "inspirationPauseUnits" },
  inspirationPressure: { label: "Insp Pres", units: "inspirationPressureUnits" },
  inspirationTime: { label: "Insp Time", units: "inspirationTimeUnits" },
  nCpap: { label: "nCPAP", units: "nCpapUnits" },
  flowPeak: { label: "Peak Flow", units: "flowPeakUnits" },
  peep: { label: "PEEP", units: "peepUnits" },
  pressureHigh: { label: "Pres High", units: "pressureHighUnits" },
  pressureLow: { label: "Pres Low", units: "pressureLowUnits" },
  psv: { label: "PSV", units: "psvUnits" },
  rate: { label: "Rate", units: "rateUnits" },
  timeHigh: { label: "Time High", units: "timeHighUnits" },
  timeLow: { label: "Time Low", units: "timeLowUnits" },
  volume: { label: "Volume", units: "volumeUnits" },
  psvTmax: { label: "Tinsp Max", units: "psvTmaxUnits" },
  waveform: { label: "Waveform" },
  minVol: { label: "%MinVol", units: "minVolUnits" },
  height: { label: "Height", units: "heightUnits" },
  flow: { label: "Flow", units: "flowUnits" },
  riseMode: { label: "Enable AutoRamp" },
  riseValue: { label: "Rise Time", units: "riseValueUnits" },
  expTrigMode: { label: "Exp Trigger Type" },
  expTrigValue: { label: "Exp Sens", units: "expTrigValueUnits" }
};

const ventAlarmSettingFields: VentFieldDefinitions<VentSettingModel> = {
  apneaInterval: { label: "Apnea Interval", units: "apneaIntervalUnits" },
  fiO2High: { label: "High FiO₂", units: "fiO2HighUnits" },
  fiO2Low: { label: "Low FiO₂", units: "fiO2LowUnits" },
  peepLow: { label: "Low PEEP", units: "peepLowUnits" },
  pPeakHigh: { label: "High Ppeak", units: "pPeakHighUnits" },
  pPeakLow: { label: "Low Ppeak", units: "pPeakLowUnits" },
  rateHigh: { label: "High Rate", units: "rateHighUnits" },
  veHigh: { label: "High Ve", units: "veHighUnits" },
  veLow: { label: "Low Ve", units: "veLowUnits" },
  vteAlarmLow: { label: "Low Vte Alarm", units: "vteAlarmLowUnits" },
  vteHigh: { label: "High Vte", units: "vteHighUnits" },
  vteLow: { label: "Low Vte", units: "vteLowUnits" },
  etCO2Low: { label: "Low EtCO₂", units: "etCO2LowUnits" },
  etCO2High: { label: "High EtCO₂", units: "etCO2HighUnits" },
  rateLow: { label: "Low Rate", units: "rateLowUnits" },
  spO2Low: { label: "Low SpO₂", units: "spO2LowUnits" },
  spO2High: { label: "High SpO₂", units: "spO2HighUnits" }
};

export function getVentSettingOrAlarmSettingLabel(field: keyof VentSettingModel) {
  return ventSettingFields[field]?.label || ventAlarmSettingFields[field]?.label || field;
}

export function getVentMonitorFieldDefinitions(
  fields: Array<keyof VentMonitorModel>
): VentFieldInfo<VentMonitorModel>[] {
  // return the fields in the same order provided
  const defs = fields
    .map(f => {
      const def = ventMonitorFields[f];
      if (def) return { label: def.label, field: f, units: def.units };
    })
    // Exclude records that don't match
    .filter(f => !!f);

  return defs as VentFieldInfo<VentMonitorModel>[];
}

function getSettingFieldDefinitions(
  definitions: VentFieldDefinitions<VentSettingModel>,
  fields: Array<keyof VentSettingModel>
): VentFieldInfo<VentSettingModel>[] {
  // return the fields in the same order provided
  const defs = fields
    .map(f => {
      const def = definitions[f];
      if (def) return { label: def.label, field: f, units: def.units };
    })
    // Exclude records that don't match
    .filter(f => !!f);

  return defs as VentFieldInfo<VentSettingModel>[];
}

export function getVentSettingFieldDefinitions(
  fields: Array<keyof VentSettingModel>
): VentFieldInfo<VentSettingModel>[] {
  return getSettingFieldDefinitions(ventSettingFields, fields);
}

export function getVentAlarmSettingFieldDefinitions(
  fields: Array<keyof VentSettingModel>
): VentFieldInfo<VentSettingModel>[] {
  return getSettingFieldDefinitions(ventAlarmSettingFields, fields);
}
